import { isEmpty } from "lodash";
import { slices } from "@/constants/sliceNames";
import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "@/axios/defaultAxios";
import { endpoints } from "@/axios/endpoints";

import { errorMessage, successMessage } from "../actions/messages.actions";

/******** Check if model is deployed *********/

export const requestCheckIsModelDeployed = createAsyncThunk(
  `${slices.modelDeployment}/requestCheckIsModelDeployed`,
  async ({ modelId, type }, { rejectWithValue, dispatch }) => {
    try {
      const response = await axios.get(endpoints.checkDeployedModel.replace(":id", modelId));
      const result = response.data.data;
      if (isEmpty(result.deployment_error)) {
        return response.data.data;
      }
      throw new Error(result.deployment_error);
    } catch (error) {
      if (error.response?.status === 404) {
        dispatch(requestCheckServableModelsLimit({ type }));
        return rejectWithValue;
      }
      dispatch(errorMessage(error.response?.data?.error ?? error.message));
      return rejectWithValue(error.response?.data?.error ?? error.message);
    }
  }
);

export const requestCheckIsModelDeployedStatus = {
  [requestCheckIsModelDeployed.fulfilled]: (state, action) => {
    const { is_deployed, is_deploying, model_name } = action.payload;
    state.loading = false;
    state.isModelDeployed = is_deployed;
    state.isModelDeploying = is_deploying;
    state.modelName = model_name;
  },
  [requestCheckIsModelDeployed.pending]: (state, action) => {
    // state.loading = true;
    state.isModelDeployed = false;
    state.isModelExsit = false;
    state.limitExceeded = false;
    state.deploymentError = "";
  },
  [requestCheckIsModelDeployed.rejected]: (state, action) => {
    state.loading = false;
    state.deploymentError = action.payload ?? "";
    state.isModelExsit = false;
    state.isModelDeployed = false;
    state.isModelDeploying = false;
  },
};

/******** Deploy Model *********/

export const requestDeployModel = createAsyncThunk(
  `${slices.modelDeployment}/requestDeployModel`,
  async ({ modelId, data, type }, { rejectWithValue, dispatch }) => {
    try {
      const response = await axios.post(endpoints.deployModel.replace(":id", modelId), data);
      if (response.status === 200 || response.status === 204) {
        dispatch(requestServableModels({ type }));
        return response.data.data;
      }
      throw new Error(response.payload?.error);
    } catch (error) {
      dispatch(errorMessage(error.response?.data?.error ?? error.message));
      if (error.response?.data?.code === 446 || error.response?.status === 446) {
        // TODO: call check limit fulfilled only
        dispatch(requestCheckServableModelsLimit({ type }));
      }

      return rejectWithValue(error.response?.data?.error ?? error.message);
    }
  }
);

export const requestDeployModelStatus = {
  [requestDeployModel.fulfilled]: (state, action) => {
    const { model_name, model_id, dataset_id } = action.payload;
    state.loading = false;
    state.isModelExsit = true;
    state.modelName = model_name;
    state.modelId = model_id;
    state.datasetId = dataset_id;
  },
  [requestDeployModel.pending]: (state, action) => {
    state.loading = true;
    state.isModelDeploying = true;
  },
  [requestDeployModel.rejected]: (state, action) => {
    state.loading = false;
    state.error = action.payload ?? "";
    state.isModelDeploying = false;
    state.isModelDeployed = false;
    state.isModelExsit = false;
  },
};

/******** Get servable models *********/

export const requestServableModels = createAsyncThunk(
  `${slices.modelDeployment}/requestServableModels`,
  async (params, { rejectWithValue, dispatch }) => {
    try {
      const response = await axios.get(endpoints.servableModelsList, { params });
      return response.data.data;
    } catch (error) {
      dispatch(errorMessage(error.response?.data?.error ?? error.message));
      return rejectWithValue(error.response?.data?.error ?? error.message);
    }
  }
);

export const requestServableModelsStatus = {
  [requestServableModels.fulfilled]: (state, action) => {
    state.loading = false;
    state.servableModels = action.payload;
  },
  [requestServableModels.pending]: (state, action) => {
    state.loading = true;
  },
  [requestServableModels.rejected]: (state, action) => {
    state.loading = false;
    state.error = action.payload ?? "";
    state.isModelDeploying = false;
    state.isModelDeployed = false;
    state.isModelExsit = false;
  },
};

/******* Delete deployed servable model ********/

export const requestDeleteServableModel = createAsyncThunk(
  `${slices.modelDeployment}/requestDeleteServableModel`,
  async ({ modelId, check = true, type }, { rejectWithValue, dispatch }) => {
    try {
      const response = await axios.delete(endpoints.deployModel.replace(":id", modelId));
      if (check) {
        dispatch(requestCheckIsModelDeployed({ modelId, type }));
        dispatch(requestServableModels({ type }));
        dispatch(requestCheckServableModelsLimit({ type }));
      }
      dispatch(successMessage(response.data.message));
      return response.data?.data;
    } catch (error) {
      dispatch(errorMessage(error.response?.data?.error ?? error.message));
      if (error.response?.data?.code === 447) {
        return rejectWithValue({
          error: error.response?.data?.error ?? error.message,
          isConnected: true,
        });
      }
      return rejectWithValue({
        error: error.response?.data?.error ?? error.message,
        isConnected: false,
      });
    }
  }
);

export const requestDeleteServableModelStatus = {
  [requestDeleteServableModel.fulfilled]: (state, action) => {
    state.isModelDeleting = false;
    state.isModelConnectedToMonitor = false;
  },
  [requestDeleteServableModel.pending]: (state, action) => {
    state.isModelDeleting = true;
    state.isModelConnectedToMonitor = false;
  },
  [requestDeleteServableModel.rejected]: (state, action) => {
    state.isModelDeleting = false;
    state.isModelConnectedToMonitor = action.payload.isConnected;
  },
};

/******** Delete machine model connection *********/

export const requestDeleteMachineModelConnection = createAsyncThunk(
  `${slices.modelDeployment}/requestDeleteMachineModelConnection`,
  async (cameraId, { rejectWithValue, dispatch }) => {
    try {
      const response = await axios.delete(endpoints.deleteCameraModelConnection.replace(":id", cameraId));
      if (response.status === 200) {
        dispatch(successMessage(response.data.message));
        return response.data?.data;
      }
      throw new Error(response.data?.error);
    } catch (error) {
      dispatch(errorMessage(error.response?.data?.error ?? error.message));
      return rejectWithValue(error.response?.data?.error ?? error.message);
    }
  }
);

export const requestDeleteMachineModelConnectionStatus = {
  [requestDeleteMachineModelConnection.fulfilled]: (state, action) => {
    state.loading = false;
    state.isModelDeploying = false;
    state.isModelDeployed = false;
    state.isModelExsit = false;
  },
  [requestDeleteMachineModelConnection.pending]: (state, action) => {
    state.loading = true;
  },
  [requestDeleteMachineModelConnection.rejected]: (state, action) => {
    state.loading = false;
    state.error = action.payload ?? "";
    state.isModelDeploying = false;
    state.isModelDeployed = false;
    state.isModelExsit = false;
  },
};

/******** Check servable models limit*********/

export const requestCheckServableModelsLimit = createAsyncThunk(
  `${slices.modelDeployment}/requestCheckServableModelsLimit`,
  async (params = {}, { rejectWithValue, dispatch }) => {
    try {
      const response = await axios.get(endpoints.checkDeployModelLimit, { params });
      return response.data?.data;
    } catch (error) {
      dispatch(errorMessage(error.response?.data?.error ?? error.message));
      return rejectWithValue(error.response?.data?.error ?? error.message);
    }
  }
);

export const requestCheckServableModelsLimitStatus = {
  [requestCheckServableModelsLimit.fulfilled]: (state, action) => {
    // eslint-disable-next-line no-console
    console.log("limit check status: ", action.payload);
    state.loading = false;
    state.limitExceeded = action.payload.limited_reached;
    state.maxAllowedServableModels = action.payload.allowed_number;
    state.deployedModels = action.payload.deployed_models;
    state.isModelDeploying = false;
  },
  [requestCheckServableModelsLimit.pending]: (state, action) => {
    state.loading = false;
    state.limitExceeded = false;
    state.isModelDeployed = false;
    state.isModelExsit = false;
    state.isModelDeploying = false;
    state.maxAllowedServableModels = 0;
    state.deployedModels = 0;
  },
  [requestCheckServableModelsLimit.rejected]: (state, action) => {
    state.loading = false;
    state.limitExceeded = false;
    state.isModelDeployed = false;
    state.isModelExsit = false;
    state.isModelDeploying = false;
    state.maxAllowedServableModels = 0;
    state.deployedModels = 0;
  },
};

/******** Try again deployment *********/

export const requestTryAgainDeployment = (modelId, data, type) => {
  return async (dispatch) => {
    try {
      dispatch(requestDeleteServableModel({ modelId, check: false, type })).then((res) => {
        if (!res.error) dispatch(requestDeployModel({ modelId, data, type }));
      });
    } catch (error) {
      dispatch(errorMessage(error.response?.data?.error));
    }
  };
};

/************* Undeploy model **************/

export const requestUndeployModel = createAsyncThunk(
  `${slices.modelDeployment}/requestUndeployModel`,
  async (modelId, { rejectWithValue, dispatch }) => {
    try {
      const response = await axios.delete(endpoints.deployModel.replace(":id", modelId));
      dispatch(successMessage("Model deployment cancelled"));
      return response.data.data;
    } catch (error) {
      dispatch(errorMessage("Model cancel deployment failed"));
    }
  }
);

export const requestUndeployModelStatus = {
  [requestUndeployModel.fulfilled]: (state, action) => {
    state.isModelUndeploying = false;
    state.isModelDeploying = false;
  },
  [requestUndeployModel.rejected]: (state, action) => {
    state.isModelUndeploying = false;
    state.isModelDeploying = true;
  },
  [requestUndeployModel.pending]: (state, action) => {
    state.isModelUndeploying = true;
    state.isModelDeploying = true;
  },
};
